import {createRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import {
  ProteinCreationCellValueType,
  ProteinCreationRow,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import type {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {IGNORE_FIELD_ID} from 'spreadsheet-import-wizard/shared/ColumnMapper';

/**
 * Map each spreadsheet header to the table column whose name matches it exactly. Headers that match no
 * column, or match more than one, are left unmapped.
 */
export function getExactHeaderMappings(
  headers: ReadonlyArray<string>,
  columns: ReadonlyArray<StructuredTableColumn>
): Record<string, string> {
  const columnIdsByName = new Map<string, Array<string>>();
  for (const column of columns) {
    const ids = columnIdsByName.get(column.name) ?? [];
    ids.push(column.provenanceUuid);
    columnIdsByName.set(column.name, ids);
  }

  return Object.fromEntries(
    headers.flatMap((header) => {
      const matchingColumnIds = columnIdsByName.get(header);
      return matchingColumnIds?.length === 1 ? [[header, matchingColumnIds[0]]] : [];
    })
  );
}

export function areAllHeadersMapped(
  headers: ReadonlyArray<string>,
  mappings: Record<string, string>
): boolean {
  return headers.every((header) => mappings[header] != null);
}

/**
 * Convert parsed spreadsheet rows into table rows, filling each column from its mapped header. Columns
 * without a mapped header are left empty.
 */
export function convertSpreadsheetRowsToProteinCreationRows({
  columns,
  mappings,
  rows,
}: {
  columns: ReadonlyArray<StructuredTableColumn>;
  /** Spreadsheet header to column `provenanceUuid`, or `IGNORE_FIELD_ID`. */
  mappings: Record<string, string>;
  rows: ReadonlyArray<Record<string, string>>;
}): Array<ProteinCreationRow> {
  const sourceHeaderByColumnId = new Map(
    Object.entries(mappings).flatMap(([header, columnId]) =>
      columnId === IGNORE_FIELD_ID ? [] : [[columnId, header]]
    )
  );

  return rows.map((row) =>
    createRow(columns, (column) => {
      const sourceHeader = sourceHeaderByColumnId.get(column.provenanceUuid);
      const text = sourceHeader != null ? (row[sourceHeader] ?? '') : '';
      return text !== ''
        ? {type: ProteinCreationCellValueType.RAW_TEXT, text}
        : {type: ProteinCreationCellValueType.EMPTY};
    })
  );
}
